﻿import React, { useEffect, useMemo, useState } from 'react'
import { Text, Heading, Input, LoadingSpinner, SortableTableHeader, Button } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { supabase } from '../lib/supabase'
import { ExclamationTriangleIcon, CalendarIcon } from '@heroicons/react/24/outline'

interface DicionarioRow {
  id: number
  created_at: string
  nome_pasi: string
  nome_mte: string
  id_pasi: number
}

const CCTCoberturasDicionario: React.FC = () => {
  const [rows, setRows] = useState<DicionarioRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filtros simples
  const [searchPasi, setSearchPasi] = useState('')
  const [searchMte, setSearchMte] = useState('')
  const [searchIdPasi, setSearchIdPasi] = useState('')

  // Ordenação
  const [sortBy, setSortBy] = useState<'created_at' | 'id' | 'nome_pasi' | 'nome_mte' | 'id_pasi'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        // Tenta com o nome esperado
        let { data, error } = await supabase
          .from('cct_coberturas_dicionario')
          .select('*')
          .order('created_at', { ascending: false })

        // Se a tabela não existir, tenta variação "ect_coberturas_dicionario"
        if (error && (error as any).code === '42P01') {
          const fallback = await supabase
            .from('ect_coberturas_dicionario')
            .select('*')
            .order('created_at', { ascending: false })
          data = fallback.data as any
          error = fallback.error as any
        }

        if (error) {
          throw error
        }
        setRows((data as DicionarioRow[]) || [])
      } catch (err: any) {
        setError(err.message || 'Erro ao buscar dados')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const onSort = (key: typeof sortBy) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortOrder('asc')
    }
  }

  const filtered = useMemo(() => {
    const normalized = rows.filter((r) => {
      const nomePasi = (r.nome_pasi || '').toLowerCase()
      const nomeMte = (r.nome_mte || '').toLowerCase()
      const matchPasi = nomePasi.includes(searchPasi.toLowerCase())
      const matchMte = nomeMte.includes(searchMte.toLowerCase())
      const matchIdPasi = searchIdPasi ? String(r.id_pasi) === searchIdPasi.trim() : true
      return matchPasi && matchMte && matchIdPasi
    })

    const sorted = [...normalized].sort((a, b) => {
      const dir = sortOrder === 'asc' ? 1 : -1
      const va: any = (a as any)[sortBy]
      const vb: any = (b as any)[sortBy]
      if (va < vb) return -1 * dir
      if (va > vb) return 1 * dir
      return 0
    })

    return sorted
  }, [rows, searchPasi, searchMte, searchIdPasi, sortBy, sortOrder])

  // Estatísticas
  const stats = useMemo(() => {
    const total = rows.length
    const comMte = rows.filter(r => (r.nome_mte || '').trim() !== '').length
    const semMte = total - comMte
    return { total, comMte, semMte }
  }, [rows])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <Text size="lg" className="text-red-600 dark:text-red-400">
            Erro ao carregar dados: {error}
          </Text>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="ccts_coberturas_dicionario" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <Heading as="h2" size="2xl">CCTs - Coberturas: Dicionário</Heading>
                  <Text size="sm" color="muted">Mapeamento de termos entre PASI e MTE</Text>
                </div>
              </div>
            </div>

            {/* Topo Colorido - Valores */}
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">{stats.total}</Text>
                    <Text size="sm" className="text-white">Total de Registros</Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">{stats.comMte}</Text>
                    <Text size="sm" className="text-white">Nomes MTE Encontrados</Text>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-lg p-6 text-white">
                  <div className="text-center">
                    <Text size="3xl" className="text-white font-bold mb-2">{stats.semMte}</Text>
                    <Text size="sm" className="text-white">Nomes MTE Não Encontrados</Text>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtros */}
            <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input 
                  placeholder="Filtrar por nome PASI"
                  value={searchPasi}
                  onChange={(e) => setSearchPasi(e.target.value)}
                />
                <Input 
                  placeholder="Filtrar por nome MTE"
                  value={searchMte}
                  onChange={(e) => setSearchMte(e.target.value)}
                />
                <Input 
                  placeholder="Filtrar por id_pasi"
                  value={searchIdPasi}
                  onChange={(e) => setSearchIdPasi(e.target.value)}
                />
                <div className="flex items-center">
                  <Button variant="outline" onClick={() => { setSearchPasi(''); setSearchMte(''); setSearchIdPasi('') }}>Limpar</Button>
                </div>
              </div>
            </div>

            {/* Tabela */}
            <div className="flex-1 p-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/30">
                      <tr>
                        <SortableTableHeader sortKey="id" currentSort={sortBy} sortOrder={sortOrder} onSort={(k) => onSort(k as any)} className="w-24">ID</SortableTableHeader>
                        <SortableTableHeader sortKey="created_at" currentSort={sortBy} sortOrder={sortOrder} onSort={(k) => onSort(k as any)} className="w-48">Criado em</SortableTableHeader>
                        <SortableTableHeader sortKey="nome_pasi" currentSort={sortBy} sortOrder={sortOrder} onSort={(k) => onSort(k as any)}>Nome PASI</SortableTableHeader>
                        <SortableTableHeader sortKey="nome_mte" currentSort={sortBy} sortOrder={sortOrder} onSort={(k) => onSort(k as any)}>Nome MTE</SortableTableHeader>
                        <SortableTableHeader sortKey="id_pasi" currentSort={sortBy} sortOrder={sortOrder} onSort={(k) => onSort(k as any)} className="w-32">ID PASI</SortableTableHeader>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filtered.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100">{row.id}</td>
                          <td className="px-6 py-3 text-sm text-gray-600 dark:text-gray-300 flex items-center space-x-2">
                            <CalendarIcon className="w-4 h-4" />
                            <span>{new Date(row.created_at).toLocaleString()}</span>
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100">{row.nome_pasi}</td>
                          <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100">{row.nome_mte}</td>
                          <td className="px-6 py-3 text-sm text-gray-900 dark:text-gray-100">{row.id_pasi}</td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400">Nenhum registro encontrado</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </PermissionGuard>
    </div>
  )
}

export default CCTCoberturasDicionario


